import { getProductsByBrand } from 'common/api/e-comm/brands';
import { APIResponse } from 'common/api/models';
import { Product } from 'common/api/e-comm/models/Product';
import { createAPIError } from 'common/api/utils/createAPIError';
import { uniq } from 'common/utils';

export interface BrandProductsResult {
    brandId: number;
    response: APIResponse<Product[]>;
}

export const normalizeBrandIds = (brandIds: number[]) =>
    uniq(brandIds.filter((id) => Number.isFinite(id))).sort((a, b) => a - b);

// Never rejects: callers sharing an in-flight promise must all settle.
const fetchProductsSafely = (brandId: number): Promise<APIResponse<Product[]>> =>
    getProductsByBrand(brandId).catch((e: Error) => ({ error: createAPIError(null, e) }));

export const createProductsLoader = () => {
    const inFlight = new Map<number, Promise<APIResponse<Product[]>>>();

    const loadBrand = (brandId: number) => {
        const pending = inFlight.get(brandId);
        if (pending) {
            return pending;
        }

        // Only dedupes concurrent calls; the persistent cache lives in Redux.
        // fetchProductsSafely never rejects, so .then is enough to settle the entry.
        const request: Promise<APIResponse<Product[]>> = fetchProductsSafely(brandId).then(
            (response) => {
                // clear() may have replaced this entry; only remove our own promise.
                if (inFlight.get(brandId) === request) {
                    inFlight.delete(brandId);
                }
                return response;
            }
        );
        inFlight.set(brandId, request);

        return request;
    };

    return {
        loadProducts: async (brandIds: number[]): Promise<BrandProductsResult[]> => {
            const sortedBrandIds = normalizeBrandIds(brandIds);
            const responses = await Promise.all(sortedBrandIds.map(loadBrand));

            return sortedBrandIds.map((brandId, index) => ({
                brandId,
                response: responses[index],
            }));
        },
        clear: () => inFlight.clear(),
    };
};

export const productsLoader = createProductsLoader();
